import { zodResolver } from "@hookform/resolvers/zod";
import {
  LangKey,
  SUPPORTED_LANGUAGE_CODES,
  SUPPORTED_LANGUAGES,
  SupportedLocale,
  useTranslation,
} from "@vixi/localization";
import {
  Button,
  Divider,
  FieldLabel,
  FormHeading,
  LoadingSpinner,
  PageMainContent,
  RadioGroup,
  RadioGroupItemWithLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextInput,
} from "@vixi/ui";
import { Controller, useController, useForm } from "react-hook-form";
import { z } from "zod";

const toneOptions: LangKey[] = ["Tone.Funny", "Tone.Formal", "Tone.Enthusiastic"];

const validationSchema = z.object({
  about: z.string().min(1),
  audience: z.string().min(1),
  tone: z.string().min(1),
  lang: z.enum(SUPPORTED_LANGUAGE_CODES),
});
export type BookInfoFormValues = z.infer<typeof validationSchema>;

export default function BookInfoForm({
  onSubmit,
  isSubmitting = false,
}: {
  onSubmit: (values: BookInfoFormValues) => void;
  isSubmitting?: boolean;
}) {
  const { locale, localeDetails: lang, t } = useTranslation();

  const { register, formState, handleSubmit, control } = useForm<BookInfoFormValues>({
    defaultValues: {
      about: "",
      audience: "",
      tone: "",
      lang: locale.slice(0, 2) as SupportedLocale,
    },
    resolver: zodResolver(validationSchema),
  });

  const {
    field: { ref, ...toneField },
  } = useController({
    name: "tone",
    control,
  });

  return (
    <PageMainContent ps="large" className="flex flex-col  items-center">
      <FormHeading heading={"BookForm.Title"} />
      <Divider className="w-[50%]"/>
      {/* Content Area */}
      <form className="flex flex-col justify-center items-center gap-5 p-5 ps-5" onSubmit={handleSubmit(onSubmit)}>
        {/* About Ebook */}
        <div>
          <FieldLabel>{t("BookForm.AboutLabel")}</FieldLabel>
          <TextInput
            {...register("about")}
            className="min-w-82.5"
            placeholder={t("BookForm.AboutPlaceholder")}
          />
        </div>

        {/* Target Audience */}
        <div className="flex flex-col justify-center items-center">
          <FieldLabel className="whitespace-pre-line">{t("BookForm.TargetAudienceLabel")}</FieldLabel>
          <TextInput
            {...register("audience")}
            className="min-w-82.5"
            placeholder={t("BookForm.TargetAudiencePlaceholder")}
          />
        </div>

        {/* Language */}
        <div>
          <FieldLabel>{t("BookForm.LanguageLabel")}</FieldLabel>
          <Controller
            name="lang"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <Select {...field}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SUPPORTED_LANGUAGES).map(([code, { name }]) => (
                    <SelectItem key={code} value={code}>
                      {lang.code === code
                        ? name
                        : `${t(`Language.${code as SupportedLocale}`)} (${name})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        {/* Tone */}
        <div>
          <FieldLabel>{t("BookForm.ToneLabel")}</FieldLabel>

          <RadioGroup {...toneField} className="flex flex-wrap gap-6">
            {toneOptions.map((option) => (
              <RadioGroupItemWithLabel key={option} value={option} label={t(option)} />
            ))}
          </RadioGroup>
        </div>

        {formState.isValid && (
          <Button
            variant="contained"
            size="large"
            type="submit"
            disabled={isSubmitting}
            className="absolute end-20 bottom-12.5"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner className="w-5 h-5" />
                {t("LoadingMessage.Loading") || "Loading..."}
              </span>
            ) : (
              t("Action.Next")
            )}
          </Button>
        )}
      </form>
    </PageMainContent>
  );
}
